/**
 * PWA-Install-/Homescreen-Onboarding (NT-PUSH-PWA-01).
 *
 * Zweck: dezenter, wegklickbarer Hinweis, die App zu installieren/zum Homescreen
 * hinzuzufügen — Voraussetzung dafür, dass Push später (3/5) überhaupt angeboten werden
 * kann (iOS erlaubt Web-Push ausschließlich in einer zum Homescreen hinzugefügten PWA).
 * Liefert hier noch KEINE Push-Logik — nur das Installations-Onboarding selbst.
 *
 * Zwei Pfade:
 *   - Android/Desktop-Chrome: `beforeinstallprompt` abfangen, Banner mit "INSTALLIEREN"-
 *     Button zeigt den nativen Install-Prompt on demand (kein Auto-Popup beim Laden).
 *   - iOS-Safari: kein `beforeinstallprompt` verfügbar — Banner mit Text-Hinweis
 *     "Über TEILEN → Zum Home-Bildschirm hinzufügen" (kein Screenshot/Icon-Grafik nötig).
 * Bereits standalone (egal auf welchem Weg installiert) → Banner erscheint nie.
 */

import { PWA_APP_NAME } from '../pwa.constants.js';
import { getConsent } from './cookie-banner.js';

const STORAGE_KEY = 'nt_pwa_install_dismissed';
// Kurze Verzögerung bevor der iOS-Hinweis erscheint — kein Auto-Popup direkt beim Laden.
const IOS_HINT_DELAY_MS = 4_000;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
// Wartet auf eine bereits fällige Anzeige, bis die Cookie-Entscheidung getroffen ist (siehe
// attemptShow) — verhindert, dass zwei Banner gleichzeitig am unteren Rand stapeln.
let pendingKind: 'ios' | 'android' | null = null;
let consentWatcher: MutationObserver | null = null;

// ─── Umgebungs-Erkennung ──────────────────────────────────────────────────────

// Exportiert (auch von der Push-Prefs-UI in settings.ts genutzt, NT-PUSH-PREFS-UI-01) — echte
// UA-Sniffing-/Standalone-Erkennungslogik, keine reine Anzeige-Konstante. Deshalb hier zentral
// gehalten statt an zwei Stellen dupliziert (anders als das übliche "kein Shared-Import"-Muster
// für Labels/Info-Texte).

export function isIOS(): boolean {
  const ua = navigator.userAgent;
  // iPadOS 13+ meldet sich als "Macintosh", ist aber über maxTouchPoints von echtem macOS
  // unterscheidbar (navigator.platform ist deprecated, daher UA-basiert statt darüber).
  const isIPadOS = ua.includes('Macintosh') && navigator.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(ua) || isIPadOS;
}

/** Mobile Safari, nicht Chrome/Firefox/Edge auf iOS (die haben trotz WebKit eigene UA-Token). */
function isIOSSafari(): boolean {
  if (!isIOS()) return false;
  return !/CriOS|FxiOS|EdgiOS|OPiOS/.test(navigator.userAgent);
}

export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function isDismissed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function markDismissed(): void {
  try {
    localStorage.setItem(STORAGE_KEY, '1');
  } catch {
    // localStorage nicht verfügbar (z.B. privater Modus) — Banner erscheint dann erneut, kein harter Fehler.
  }
}

// ─── Rendering ────────────────────────────────────────────────────────────────

function removeBanner(): void {
  document.getElementById('nt-pwa-install-hint')?.remove();
}

function renderBanner(kind: 'ios' | 'android'): void {
  if (document.getElementById('nt-pwa-install-hint')) return;

  const banner = document.createElement('div');
  banner.id = 'nt-pwa-install-hint';
  banner.setAttribute('role', 'dialog');
  banner.setAttribute('aria-modal', 'false');
  banner.setAttribute('aria-label', 'App installieren');

  const text = kind === 'ios'
    ? 'Als App installieren: Tippe unten auf TEILEN &rarr; „Zum Home-Bildschirm"'
    : `Installiere ${PWA_APP_NAME} als App für schnelleren Zugriff.`;

  banner.innerHTML = `
    <div class="pwa-hint-content">
      <p class="pwa-hint-text">${text}</p>
      <div class="pwa-hint-actions">
        ${kind === 'android' ? '<button type="button" id="pwa-hint-install" class="pwa-hint-btn pwa-hint-btn--primary">INSTALLIEREN</button>' : ''}
        <button type="button" id="pwa-hint-dismiss" class="pwa-hint-btn pwa-hint-btn--secondary">SPÄTER</button>
      </div>
    </div>
  `;

  document.body.appendChild(banner);

  document.getElementById('pwa-hint-dismiss')?.addEventListener('click', () => {
    markDismissed();
    removeBanner();
  });

  document.getElementById('pwa-hint-install')?.addEventListener('click', () => {
    void (async () => {
      if (!deferredInstallPrompt) return;
      await deferredInstallPrompt.prompt();
      await deferredInstallPrompt.userChoice;
      deferredInstallPrompt = null;
      markDismissed();
      removeBanner();
    })();
  });
}

// ─── Sichtbarkeits-Steuerung ───────────────────────────────────────────────────

/**
 * Versucht den Banner zu zeigen. Steht die Cookie-Entscheidung noch aus, wird der Versuch
 * gemerkt und automatisch wiederholt, sobald der Cookie-Banner aus dem DOM verschwindet
 * (statt beide Banner gleichzeitig am unteren Rand zu stapeln).
 */
function attemptShow(kind: 'ios' | 'android'): void {
  if (isStandalone() || isDismissed()) return;

  if (getConsent() === null) {
    pendingKind = kind;
    watchForConsentDecision();
    return;
  }

  renderBanner(kind);
}

function watchForConsentDecision(): void {
  if (consentWatcher) return;
  consentWatcher = new MutationObserver(() => {
    if (document.getElementById('nt-cookie-banner') !== null) return;
    consentWatcher?.disconnect();
    consentWatcher = null;
    const kind = pendingKind;
    pendingKind = null;
    if (kind) attemptShow(kind);
  });
  consentWatcher.observe(document.body, { childList: true });
}

// ─── Init ─────────────────────────────────────────────────────────────────────

/** PWA-Install-Onboarding initialisieren. Einmal beim App-Start aufrufen. */
export function initPwaInstallHint(): void {
  if (isStandalone() || isDismissed()) return;

  if (isIOSSafari()) {
    setTimeout(() => { attemptShow('ios'); }, IOS_HINT_DELAY_MS);
    return;
  }

  // Android/Desktop-Chrome: Timing entscheidet der Browser (Engagement-Heuristik) — kein
  // eigener Timer nötig, das Event feuert ohnehin nie direkt beim ersten Laden.
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredInstallPrompt = e as BeforeInstallPromptEvent;
    attemptShow('android');
  });

  // Erfolgreich installiert (z.B. über Browser-eigenes Menü statt unseren Banner) → Banner
  // endgültig unterdrücken, damit kein toter "INSTALLIEREN"-Button mehr erscheinen kann.
  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
    pendingKind = null;
    markDismissed();
    removeBanner();
  });
}
